import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function DigitalMarketingGuidePage() {
  const strategies = [
    {
      title: 'Search Engine Optimization (SEO)',
      icon: 'ri-search-line',
      description: 'Improve your website\'s visibility in search results',
      tips: [
        'Optimize for local El Paso keywords (e.g., "plumber in El Paso TX")',
        'Create location-specific content about El Paso neighborhoods',
        'Build citations on local directories and business listings',
        'Ensure mobile-friendly website design',
        'Focus on page speed and user experience',
        'Use schema markup for local business information'
      ]
    },
    {
      title: 'Google Business Profile',
      icon: 'ri-google-line',
      description: 'Essential for local visibility in El Paso',
      tips: [
        'Claim and verify your Google Business Profile listing',
        'Add accurate business hours, address, and phone number',
        'Upload high-quality photos of your business and products',
        'Encourage and respond to customer reviews',
        'Post regular updates about offers and events',
        'Use Google Posts to share news and promotions'
      ]
    },
    {
      title: 'Email Marketing',
      icon: 'ri-mail-line',
      description: 'Build relationships with your El Paso customers',
      tips: [
        'Build an email list through website sign-ups and in-store promotions',
        'Segment your audience for targeted campaigns',
        'Create compelling subject lines and preview text',
        'Personalize content based on customer behavior',
        'Include clear calls-to-action in every email',
        'Test send times to find when El Paso customers engage most'
      ]
    },
    {
      title: 'Pay-Per-Click (PPC) Advertising',
      icon: 'ri-advertisement-line',
      description: 'Drive immediate traffic and leads',
      tips: [
        'Start with Google Ads for local search campaigns',
        'Use geo-targeting to focus on El Paso and surrounding areas',
        'Create compelling ad copy with local references',
        'Set up conversion tracking to measure ROI',
        'Test different ad variations and landing pages',
        'Monitor and adjust bids based on performance'
      ]
    }
  ];

  const platforms = [
    {
      name: 'Google Analytics',
      description: 'Track website traffic and user behavior',
      icon: 'ri-line-chart-line'
    },
    {
      name: 'Google Search Console',
      description: 'Monitor search performance and technical issues',
      icon: 'ri-search-eye-line'
    },
    {
      name: 'Mailchimp',
      description: 'Email marketing automation platform',
      icon: 'ri-mail-send-line'
    },
    {
      name: 'SEMrush',
      description: 'Comprehensive SEO and competitive analysis',
      icon: 'ri-bar-chart-box-line'
    },
    {
      name: 'Canva',
      description: 'Create professional marketing graphics',
      icon: 'ri-image-edit-line'
    },
    {
      name: 'Hootsuite',
      description: 'Manage social media campaigns',
      icon: 'ri-calendar-schedule-line'
    }
  ];

  const localTips = [
    {
      title: 'Target Border Region',
      description: 'Consider bilingual marketing to reach both El Paso and Juárez audiences',
      icon: 'ri-global-line'
    },
    {
      title: 'Leverage Local Events',
      description: 'Promote around Sun Bowl, UTEP events, and local festivals',
      icon: 'ri-calendar-event-line'
    },
    {
      title: 'Military Community',
      description: 'Fort Bliss represents a significant market segment',
      icon: 'ri-shield-star-line'
    },
    {
      title: 'Mobile-First Approach',
      description: 'El Paso has high mobile usage rates - optimize accordingly',
      icon: 'ri-smartphone-line'
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Digital Marketing Guide",
        "item": "https://elpasotexasbusiness.com/resources/digital-marketing-guide"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Digital Marketing Guide for El Paso Small Businesses"
        description="Master digital marketing for your El Paso business. Learn SEO, social media marketing, Google Ads, email campaigns, and local marketing strategies to reach border market customers."
        canonical="https://elpasotexasbusiness.com/resources/digital-marketing-guide"
        ogImage="https://public.readdy.ai/ai/img_res/2188d180592de10fb40728b5714b42b1.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Digital Marketing Guide</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Build a strong online presence and reach your El Paso customers effectively through proven digital marketing strategies. This comprehensive guide covers everything from SEO to paid advertising.
              </p>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Core Digital Marketing Strategies</h2>
              <div className="space-y-12">
                {strategies.map((strategy, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${strategy.icon} text-3xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{strategy.title}</h3>
                        <p className="text-base text-gray-600">{strategy.description}</p>
                      </div>
                    </div>
                    <div className="ml-22">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Key Actions:</h4>
                      <ul className="space-y-3">
                        {strategy.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start space-x-3">
                            <i className="ri-checkbox-circle-fill text-[#D2691E] text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Essential Digital Marketing Tools</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {platforms.map((platform, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${platform.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{platform.name}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{platform.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">El Paso-Specific Marketing Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {localTips.map((tip, index) => (
                  <div key={index} className="bg-[#1B3A3A] rounded-2xl p-8 text-white">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${tip.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{tip.title}</h3>
                    <p className="text-sm text-white/80 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Getting Started Checklist</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  'Set up Google Business Profile',
                  'Install Google Analytics on your website',
                  'Create social media business accounts',
                  'Build an email subscriber list',
                  'Optimize website for mobile devices',
                  'Research local keywords for SEO',
                  'Set up conversion tracking',
                  'Create a content calendar',
                  'Monitor online reviews and respond',
                  'Test and measure all campaigns'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-line text-[#D2691E] text-2xl flex-shrink-0"></i>
                    <span className="text-base text-gray-700 pt-0.5">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 bg-[#1B3A3A] rounded-3xl p-12 text-center">
              <h3 className="text-4xl font-bold text-white mb-4">Need Marketing Support?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Our team can help you develop and execute a digital marketing strategy tailored to your El Paso business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg whitespace-nowrap"
              >
                <span>Get in Touch</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}